/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import {
  collectExtraKeys as collectExtraKeys$,
  safeParse,
} from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as components from "../components/index.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

export const Status = {
  Queued: "queued",
  InProgress: "in_progress",
  Finalizing: "finalizing",
  Completed: "completed",
  Failed: "failed",
  Cancelling: "cancelling",
  Cancelled: "cancelled",
} as const;
export type Status = ClosedEnum<typeof Status>;

export type ListEvalRunsRequest = {
  after?: string | undefined;
  limit?: number | undefined;
  suiteId?: string | undefined;
  suiteVersion?: number | undefined;
  status?: Status | undefined;
  /**
   * Optional external end-user identifier forwarded by the API gateway.
   */
  xOnBehalfOf?: string | undefined;
};

export type ListEvalRunsStop = string | Array<string>;

export const ListEvalRunsTruncation = {
  Auto: "auto",
  Disabled: "disabled",
} as const;
export type ListEvalRunsTruncation = ClosedEnum<typeof ListEvalRunsTruncation>;

export const ListEvalRunsServiceTier = {
  Auto: "auto",
  Default: "default",
  Flex: "flex",
  Priority: "priority",
} as const;
export type ListEvalRunsServiceTier = ClosedEnum<
  typeof ListEvalRunsServiceTier
>;

export type ListEvalRunsPrefillThink = boolean | string;

export type ListEvalRunsGeneration = {
  instructions?: string | undefined;
  temperature?: number | undefined;
  topP?: number | undefined;
  maxOutputTokens?: number | undefined;
  /**
   * lm-eval alias for max_output_tokens.
   */
  maxGenToks?: number | undefined;
  stop?: string | Array<string> | undefined;
  /**
   * lm-eval generate_until stop sequences.
   */
  until?: Array<string> | undefined;
  maxToolCalls?: number | undefined;
  reasoning?: any | undefined;
  text?: any | undefined;
  tools?: Array<any | null> | undefined;
  toolChoice?: any | undefined;
  parallelToolCalls?: boolean | undefined;
  truncation?: ListEvalRunsTruncation | undefined;
  serviceTier?: ListEvalRunsServiceTier | undefined;
  presencePenalty?: number | undefined;
  frequencyPenalty?: number | undefined;
  topK?: number | undefined;
  minP?: number | undefined;
  repetitionPenalty?: number | undefined;
  doSample?: boolean | undefined;
  extraBody?: { [k: string]: any } | undefined;
  chatTemplateKwargs?: { [k: string]: any } | undefined;
  prefillThink?: boolean | string | undefined;
  useCache?: boolean | undefined;
  timeoutSeconds?: number | undefined;
  maxRetries?: number | undefined;
  maxEmptyRetries?: number | undefined;
  additionalProperties?: { [k: string]: any } | undefined;
};

export type ListEvalRunsRequestCounts = {
  total: number;
  completed: number;
  failed: number;
};

export type ListEvalRunsData = {
  id: string;
  object: "eval.run";
  suiteId: string;
  suiteVersion: number;
  suiteVersionId: string;
  /**
   * The org that owns this run.
   */
  orgId: string;
  /**
   * The team that owns this run.
   */
  teamId: string;
  status: components.EvalRunStatus;
  models: Array<string>;
  taskIds: Array<string> | null;
  judgeModel: string | null;
  embeddingModel: string | null;
  generation: ListEvalRunsGeneration;
  requestCounts: ListEvalRunsRequestCounts;
  metrics: { [k: string]: any } | null;
  error: { [k: string]: any } | null;
  artifactFileIds: Array<string>;
  metadata: { [k: string]: string };
  createdAt: number;
  startedAt: number | null;
  completedAt: number | null;
  cancelledAt: number | null;
  failedAt: number | null;
};

/**
 * OK
 */
export type ListEvalRunsResponseBody = {
  object: "list";
  data: Array<ListEvalRunsData>;
  firstId?: string | undefined;
  lastId?: string | undefined;
  hasMore: boolean;
};

/** @internal */
export const Status$inboundSchema: z.ZodNativeEnum<typeof Status> = z
  .nativeEnum(Status);
/** @internal */
export const Status$outboundSchema: z.ZodNativeEnum<typeof Status> =
  Status$inboundSchema;

/** @internal */
export const ListEvalRunsRequest$inboundSchema: z.ZodType<
  ListEvalRunsRequest,
  z.ZodTypeDef,
  unknown
> = z.object({
  after: z.string().optional(),
  limit: z.number().int().default(20),
  suite_id: z.string().optional(),
  suite_version: z.number().int().optional(),
  status: Status$inboundSchema.optional(),
  "X-On-Behalf-Of": z.string().optional(),
}).transform((v) => {
  return remap$(v, {
    "suite_id": "suiteId",
    "suite_version": "suiteVersion",
    "X-On-Behalf-Of": "xOnBehalfOf",
  });
});
/** @internal */
export type ListEvalRunsRequest$Outbound = {
  after?: string | undefined;
  limit: number;
  suite_id?: string | undefined;
  suite_version?: number | undefined;
  status?: string | undefined;
  "X-On-Behalf-Of"?: string | undefined;
};

/** @internal */
export const ListEvalRunsRequest$outboundSchema: z.ZodType<
  ListEvalRunsRequest$Outbound,
  z.ZodTypeDef,
  ListEvalRunsRequest
> = z.object({
  after: z.string().optional(),
  limit: z.number().int().default(20),
  suiteId: z.string().optional(),
  suiteVersion: z.number().int().optional(),
  status: Status$outboundSchema.optional(),
  xOnBehalfOf: z.string().optional(),
}).transform((v) => {
  return remap$(v, {
    suiteId: "suite_id",
    suiteVersion: "suite_version",
    xOnBehalfOf: "X-On-Behalf-Of",
  });
});

export function listEvalRunsRequestToJSON(
  listEvalRunsRequest: ListEvalRunsRequest,
): string {
  return JSON.stringify(
    ListEvalRunsRequest$outboundSchema.parse(listEvalRunsRequest),
  );
}
export function listEvalRunsRequestFromJSON(
  jsonString: string,
): SafeParseResult<ListEvalRunsRequest, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ListEvalRunsRequest$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListEvalRunsRequest' from JSON`,
  );
}

/** @internal */
export const ListEvalRunsStop$inboundSchema: z.ZodType<
  ListEvalRunsStop,
  z.ZodTypeDef,
  unknown
> = z.union([z.string(), z.array(z.string())]);
/** @internal */
export type ListEvalRunsStop$Outbound = string | Array<string>;

/** @internal */
export const ListEvalRunsStop$outboundSchema: z.ZodType<
  ListEvalRunsStop$Outbound,
  z.ZodTypeDef,
  ListEvalRunsStop
> = z.union([z.string(), z.array(z.string())]);

export function listEvalRunsStopToJSON(
  listEvalRunsStop: ListEvalRunsStop,
): string {
  return JSON.stringify(
    ListEvalRunsStop$outboundSchema.parse(listEvalRunsStop),
  );
}
export function listEvalRunsStopFromJSON(
  jsonString: string,
): SafeParseResult<ListEvalRunsStop, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ListEvalRunsStop$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListEvalRunsStop' from JSON`,
  );
}

/** @internal */
export const ListEvalRunsTruncation$inboundSchema: z.ZodNativeEnum<
  typeof ListEvalRunsTruncation
> = z.nativeEnum(ListEvalRunsTruncation);
/** @internal */
export const ListEvalRunsTruncation$outboundSchema: z.ZodNativeEnum<
  typeof ListEvalRunsTruncation
> = ListEvalRunsTruncation$inboundSchema;

/** @internal */
export const ListEvalRunsServiceTier$inboundSchema: z.ZodNativeEnum<
  typeof ListEvalRunsServiceTier
> = z.nativeEnum(ListEvalRunsServiceTier);
/** @internal */
export const ListEvalRunsServiceTier$outboundSchema: z.ZodNativeEnum<
  typeof ListEvalRunsServiceTier
> = ListEvalRunsServiceTier$inboundSchema;

/** @internal */
export const ListEvalRunsPrefillThink$inboundSchema: z.ZodType<
  ListEvalRunsPrefillThink,
  z.ZodTypeDef,
  unknown
> = z.union([z.boolean(), z.string()]);
/** @internal */
export type ListEvalRunsPrefillThink$Outbound = boolean | string;

/** @internal */
export const ListEvalRunsPrefillThink$outboundSchema: z.ZodType<
  ListEvalRunsPrefillThink$Outbound,
  z.ZodTypeDef,
  ListEvalRunsPrefillThink
> = z.union([z.boolean(), z.string()]);

export function listEvalRunsPrefillThinkToJSON(
  listEvalRunsPrefillThink: ListEvalRunsPrefillThink,
): string {
  return JSON.stringify(
    ListEvalRunsPrefillThink$outboundSchema.parse(listEvalRunsPrefillThink),
  );
}
export function listEvalRunsPrefillThinkFromJSON(
  jsonString: string,
): SafeParseResult<ListEvalRunsPrefillThink, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ListEvalRunsPrefillThink$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListEvalRunsPrefillThink' from JSON`,
  );
}

/** @internal */
export const ListEvalRunsGeneration$inboundSchema: z.ZodType<
  ListEvalRunsGeneration,
  z.ZodTypeDef,
  unknown
> = collectExtraKeys$(
  z.object({
    instructions: z.string().optional(),
    temperature: z.number().optional(),
    top_p: z.number().optional(),
    max_output_tokens: z.number().int().optional(),
    max_gen_toks: z.number().int().optional(),
    stop: z.union([z.string(), z.array(z.string())]).optional(),
    until: z.array(z.string()).optional(),
    max_tool_calls: z.number().int().optional(),
    reasoning: z.any().optional(),
    text: z.any().optional(),
    tools: z.array(z.nullable(z.any())).optional(),
    tool_choice: z.any().optional(),
    parallel_tool_calls: z.boolean().optional(),
    truncation: ListEvalRunsTruncation$inboundSchema.optional(),
    service_tier: ListEvalRunsServiceTier$inboundSchema.optional(),
    presence_penalty: z.number().optional(),
    frequency_penalty: z.number().optional(),
    top_k: z.number().int().optional(),
    min_p: z.number().optional(),
    repetition_penalty: z.number().optional(),
    do_sample: z.boolean().optional(),
    extra_body: z.record(z.any()).optional(),
    chat_template_kwargs: z.record(z.any()).optional(),
    prefill_think: z.union([z.boolean(), z.string()]).optional(),
    use_cache: z.boolean().optional(),
    timeout_seconds: z.number().int().optional(),
    max_retries: z.number().int().optional(),
    max_empty_retries: z.number().int().optional(),
  }).catchall(z.any()),
  "additionalProperties",
  true,
).transform((v) => {
  return remap$(v, {
    "top_p": "topP",
    "max_output_tokens": "maxOutputTokens",
    "max_gen_toks": "maxGenToks",
    "max_tool_calls": "maxToolCalls",
    "tool_choice": "toolChoice",
    "parallel_tool_calls": "parallelToolCalls",
    "service_tier": "serviceTier",
    "presence_penalty": "presencePenalty",
    "frequency_penalty": "frequencyPenalty",
    "top_k": "topK",
    "min_p": "minP",
    "repetition_penalty": "repetitionPenalty",
    "do_sample": "doSample",
    "extra_body": "extraBody",
    "chat_template_kwargs": "chatTemplateKwargs",
    "prefill_think": "prefillThink",
    "use_cache": "useCache",
    "timeout_seconds": "timeoutSeconds",
    "max_retries": "maxRetries",
    "max_empty_retries": "maxEmptyRetries",
  });
});
/** @internal */
export type ListEvalRunsGeneration$Outbound = {
  instructions?: string | undefined;
  temperature?: number | undefined;
  top_p?: number | undefined;
  max_output_tokens?: number | undefined;
  max_gen_toks?: number | undefined;
  stop?: string | Array<string> | undefined;
  until?: Array<string> | undefined;
  max_tool_calls?: number | undefined;
  reasoning?: any | undefined;
  text?: any | undefined;
  tools?: Array<any | null> | undefined;
  tool_choice?: any | undefined;
  parallel_tool_calls?: boolean | undefined;
  truncation?: string | undefined;
  service_tier?: string | undefined;
  presence_penalty?: number | undefined;
  frequency_penalty?: number | undefined;
  top_k?: number | undefined;
  min_p?: number | undefined;
  repetition_penalty?: number | undefined;
  do_sample?: boolean | undefined;
  extra_body?: { [k: string]: any } | undefined;
  chat_template_kwargs?: { [k: string]: any } | undefined;
  prefill_think?: boolean | string | undefined;
  use_cache?: boolean | undefined;
  timeout_seconds?: number | undefined;
  max_retries?: number | undefined;
  max_empty_retries?: number | undefined;
  [additionalProperties: string]: unknown;
};

/** @internal */
export const ListEvalRunsGeneration$outboundSchema: z.ZodType<
  ListEvalRunsGeneration$Outbound,
  z.ZodTypeDef,
  ListEvalRunsGeneration
> = z.object({
  instructions: z.string().optional(),
  temperature: z.number().optional(),
  topP: z.number().optional(),
  maxOutputTokens: z.number().int().optional(),
  maxGenToks: z.number().int().optional(),
  stop: z.union([z.string(), z.array(z.string())]).optional(),
  until: z.array(z.string()).optional(),
  maxToolCalls: z.number().int().optional(),
  reasoning: z.any().optional(),
  text: z.any().optional(),
  tools: z.array(z.nullable(z.any())).optional(),
  toolChoice: z.any().optional(),
  parallelToolCalls: z.boolean().optional(),
  truncation: ListEvalRunsTruncation$outboundSchema.optional(),
  serviceTier: ListEvalRunsServiceTier$outboundSchema.optional(),
  presencePenalty: z.number().optional(),
  frequencyPenalty: z.number().optional(),
  topK: z.number().int().optional(),
  minP: z.number().optional(),
  repetitionPenalty: z.number().optional(),
  doSample: z.boolean().optional(),
  extraBody: z.record(z.any()).optional(),
  chatTemplateKwargs: z.record(z.any()).optional(),
  prefillThink: z.union([z.boolean(), z.string()]).optional(),
  useCache: z.boolean().optional(),
  timeoutSeconds: z.number().int().optional(),
  maxRetries: z.number().int().optional(),
  maxEmptyRetries: z.number().int().optional(),
  additionalProperties: z.record(z.any()).optional(),
}).transform((v) => {
  return {
    ...v.additionalProperties,
    ...remap$(v, {
      topP: "top_p",
      maxOutputTokens: "max_output_tokens",
      maxGenToks: "max_gen_toks",
      maxToolCalls: "max_tool_calls",
      toolChoice: "tool_choice",
      parallelToolCalls: "parallel_tool_calls",
      serviceTier: "service_tier",
      presencePenalty: "presence_penalty",
      frequencyPenalty: "frequency_penalty",
      topK: "top_k",
      minP: "min_p",
      repetitionPenalty: "repetition_penalty",
      doSample: "do_sample",
      extraBody: "extra_body",
      chatTemplateKwargs: "chat_template_kwargs",
      prefillThink: "prefill_think",
      useCache: "use_cache",
      timeoutSeconds: "timeout_seconds",
      maxRetries: "max_retries",
      maxEmptyRetries: "max_empty_retries",
      additionalProperties: null,
    }),
  };
});

export function listEvalRunsGenerationToJSON(
  listEvalRunsGeneration: ListEvalRunsGeneration,
): string {
  return JSON.stringify(
    ListEvalRunsGeneration$outboundSchema.parse(listEvalRunsGeneration),
  );
}
export function listEvalRunsGenerationFromJSON(
  jsonString: string,
): SafeParseResult<ListEvalRunsGeneration, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ListEvalRunsGeneration$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListEvalRunsGeneration' from JSON`,
  );
}

/** @internal */
export const ListEvalRunsRequestCounts$inboundSchema: z.ZodType<
  ListEvalRunsRequestCounts,
  z.ZodTypeDef,
  unknown
> = z.object({
  total: z.number().int(),
  completed: z.number().int(),
  failed: z.number().int(),
});
/** @internal */
export type ListEvalRunsRequestCounts$Outbound = {
  total: number;
  completed: number;
  failed: number;
};

/** @internal */
export const ListEvalRunsRequestCounts$outboundSchema: z.ZodType<
  ListEvalRunsRequestCounts$Outbound,
  z.ZodTypeDef,
  ListEvalRunsRequestCounts
> = z.object({
  total: z.number().int(),
  completed: z.number().int(),
  failed: z.number().int(),
});

export function listEvalRunsRequestCountsToJSON(
  listEvalRunsRequestCounts: ListEvalRunsRequestCounts,
): string {
  return JSON.stringify(
    ListEvalRunsRequestCounts$outboundSchema.parse(listEvalRunsRequestCounts),
  );
}
export function listEvalRunsRequestCountsFromJSON(
  jsonString: string,
): SafeParseResult<ListEvalRunsRequestCounts, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ListEvalRunsRequestCounts$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListEvalRunsRequestCounts' from JSON`,
  );
}

/** @internal */
export const ListEvalRunsData$inboundSchema: z.ZodType<
  ListEvalRunsData,
  z.ZodTypeDef,
  unknown
> = z.object({
  id: z.string(),
  object: z.literal("eval.run"),
  suite_id: z.string(),
  suite_version: z.number().int(),
  suite_version_id: z.string(),
  org_id: z.string(),
  team_id: z.string(),
  status: components.EvalRunStatus$inboundSchema,
  models: z.array(z.string()),
  task_ids: z.nullable(z.array(z.string())),
  judge_model: z.nullable(z.string()),
  embedding_model: z.nullable(z.string()),
  generation: z.lazy(() => ListEvalRunsGeneration$inboundSchema),
  request_counts: z.lazy(() => ListEvalRunsRequestCounts$inboundSchema),
  metrics: z.nullable(z.record(z.any())),
  error: z.nullable(z.record(z.any())),
  artifact_file_ids: z.array(z.string()),
  metadata: z.record(z.string()),
  created_at: z.number().int(),
  started_at: z.nullable(z.number().int()),
  completed_at: z.nullable(z.number().int()),
  cancelled_at: z.nullable(z.number().int()),
  failed_at: z.nullable(z.number().int()),
}).transform((v) => {
  return remap$(v, {
    "suite_id": "suiteId",
    "suite_version": "suiteVersion",
    "suite_version_id": "suiteVersionId",
    "org_id": "orgId",
    "team_id": "teamId",
    "task_ids": "taskIds",
    "judge_model": "judgeModel",
    "embedding_model": "embeddingModel",
    "request_counts": "requestCounts",
    "artifact_file_ids": "artifactFileIds",
    "created_at": "createdAt",
    "started_at": "startedAt",
    "completed_at": "completedAt",
    "cancelled_at": "cancelledAt",
    "failed_at": "failedAt",
  });
});
/** @internal */
export type ListEvalRunsData$Outbound = {
  id: string;
  object: "eval.run";
  suite_id: string;
  suite_version: number;
  suite_version_id: string;
  org_id: string;
  team_id: string;
  status: string;
  models: Array<string>;
  task_ids: Array<string> | null;
  judge_model: string | null;
  embedding_model: string | null;
  generation: ListEvalRunsGeneration$Outbound;
  request_counts: ListEvalRunsRequestCounts$Outbound;
  metrics: { [k: string]: any } | null;
  error: { [k: string]: any } | null;
  artifact_file_ids: Array<string>;
  metadata: { [k: string]: string };
  created_at: number;
  started_at: number | null;
  completed_at: number | null;
  cancelled_at: number | null;
  failed_at: number | null;
};

/** @internal */
export const ListEvalRunsData$outboundSchema: z.ZodType<
  ListEvalRunsData$Outbound,
  z.ZodTypeDef,
  ListEvalRunsData
> = z.object({
  id: z.string(),
  object: z.literal("eval.run"),
  suiteId: z.string(),
  suiteVersion: z.number().int(),
  suiteVersionId: z.string(),
  orgId: z.string(),
  teamId: z.string(),
  status: components.EvalRunStatus$outboundSchema,
  models: z.array(z.string()),
  taskIds: z.nullable(z.array(z.string())),
  judgeModel: z.nullable(z.string()),
  embeddingModel: z.nullable(z.string()),
  generation: z.lazy(() => ListEvalRunsGeneration$outboundSchema),
  requestCounts: z.lazy(() => ListEvalRunsRequestCounts$outboundSchema),
  metrics: z.nullable(z.record(z.any())),
  error: z.nullable(z.record(z.any())),
  artifactFileIds: z.array(z.string()),
  metadata: z.record(z.string()),
  createdAt: z.number().int(),
  startedAt: z.nullable(z.number().int()),
  completedAt: z.nullable(z.number().int()),
  cancelledAt: z.nullable(z.number().int()),
  failedAt: z.nullable(z.number().int()),
}).transform((v) => {
  return remap$(v, {
    suiteId: "suite_id",
    suiteVersion: "suite_version",
    suiteVersionId: "suite_version_id",
    orgId: "org_id",
    teamId: "team_id",
    taskIds: "task_ids",
    judgeModel: "judge_model",
    embeddingModel: "embedding_model",
    requestCounts: "request_counts",
    artifactFileIds: "artifact_file_ids",
    createdAt: "created_at",
    startedAt: "started_at",
    completedAt: "completed_at",
    cancelledAt: "cancelled_at",
    failedAt: "failed_at",
  });
});

export function listEvalRunsDataToJSON(
  listEvalRunsData: ListEvalRunsData,
): string {
  return JSON.stringify(
    ListEvalRunsData$outboundSchema.parse(listEvalRunsData),
  );
}
export function listEvalRunsDataFromJSON(
  jsonString: string,
): SafeParseResult<ListEvalRunsData, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ListEvalRunsData$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListEvalRunsData' from JSON`,
  );
}

/** @internal */
export const ListEvalRunsResponseBody$inboundSchema: z.ZodType<
  ListEvalRunsResponseBody,
  z.ZodTypeDef,
  unknown
> = z.object({
  object: z.literal("list"),
  data: z.array(z.lazy(() => ListEvalRunsData$inboundSchema)),
  first_id: z.string().optional(),
  last_id: z.string().optional(),
  has_more: z.boolean(),
}).transform((v) => {
  return remap$(v, {
    "first_id": "firstId",
    "last_id": "lastId",
    "has_more": "hasMore",
  });
});
/** @internal */
export type ListEvalRunsResponseBody$Outbound = {
  object: "list";
  data: Array<ListEvalRunsData$Outbound>;
  first_id?: string | undefined;
  last_id?: string | undefined;
  has_more: boolean;
};

/** @internal */
export const ListEvalRunsResponseBody$outboundSchema: z.ZodType<
  ListEvalRunsResponseBody$Outbound,
  z.ZodTypeDef,
  ListEvalRunsResponseBody
> = z.object({
  object: z.literal("list"),
  data: z.array(z.lazy(() => ListEvalRunsData$outboundSchema)),
  firstId: z.string().optional(),
  lastId: z.string().optional(),
  hasMore: z.boolean(),
}).transform((v) => {
  return remap$(v, {
    firstId: "first_id",
    lastId: "last_id",
    hasMore: "has_more",
  });
});

export function listEvalRunsResponseBodyToJSON(
  listEvalRunsResponseBody: ListEvalRunsResponseBody,
): string {
  return JSON.stringify(
    ListEvalRunsResponseBody$outboundSchema.parse(listEvalRunsResponseBody),
  );
}
export function listEvalRunsResponseBodyFromJSON(
  jsonString: string,
): SafeParseResult<ListEvalRunsResponseBody, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ListEvalRunsResponseBody$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListEvalRunsResponseBody' from JSON`,
  );
}
